import { useEffect, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

import { companyActions } from "../actions";
import { useCaseMatches } from "../queries";
import type { Company } from "../types";
import { defaultEntity } from "../utils";

const NONE = "__none__";

type Props = {
  company: Company;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Link unlinked cases, found by the employer name typed on them or by the
 * client's name, with the entity and job they use.
 */
export const LinkCasesDialog: FC<Props> = ({ company, open, onOpenChange }) => {
  const [name, setName] = useState("");
  const [typed, setTyped] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [entityId, setEntityId] = useState<string>("");
  const [jobId, setJobId] = useState<string>(NONE);
  const [busy, setBusy] = useState(false);
  const matches = useCaseMatches(open ? name : "");

  useEffect(() => {
    if (!open) return;
    const start = defaultEntity(company)?.legal_name ?? company.name;
    setName(start);
    setTyped(start);
    setPicked(new Set());
    setEntityId(defaultEntity(company)?.id ?? "");
    setJobId(NONE);
  }, [open, company]);

  useEffect(() => {
    const t = setTimeout(() => setName(typed), 300);
    return () => clearTimeout(t);
  }, [typed]);

  const toggle = (id: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async () => {
    setBusy(true);
    const linked = await companyActions.link(company, [...picked], {
      entity_id: entityId || null,
      job_description_id: jobId === NONE ? null : jobId
    });
    setBusy(false);
    if (linked) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="link-cases-dialog">
        <DialogHeader>
          <DialogTitle>Link cases to {company.name}</DialogTitle>
          <DialogDescription>
            Their employer details are replaced by the company record and stay
            in sync with it.
          </DialogDescription>
        </DialogHeader>
        <Input
          aria-label="Employer or client name"
          value={typed}
          placeholder="Employer or client name"
          onChange={(e) => setTyped(e.currentTarget.value)}
        />
        {matches.isLoading ? (
          <Skeleton className="h-24" />
        ) : matches.data?.length ? (
          <div className="max-h-56 overflow-y-auto rounded-md border border-line-2">
            {matches.data.map((c) => (
              <label
                key={c.individual_id}
                htmlFor={`link-${c.individual_id}`}
                className="flex cursor-pointer items-center gap-2.5 border-b border-line-2 px-3 py-2 text-sm last:border-b-0 hover:bg-fill-1"
              >
                <Checkbox
                  id={`link-${c.individual_id}`}
                  checked={picked.has(c.individual_id)}
                  onCheckedChange={() => toggle(c.individual_id)}
                  aria-label={`Link ${c.name}`}
                />
                <span className="min-w-0 flex-1 truncate">
                  {c.name}
                  {c.visa ? (
                    <span className="text-fg-3"> · {c.visa}</span>
                  ) : null}
                </span>
                <span className="truncate text-sm text-fg-3">{c.employer}</span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-sm text-fg-3">
            No unlinked cases match. Search by the client's name to find a case
            with no employer typed yet.
          </p>
        )}
        <div className="grid grid-cols-2 gap-2">
          {company.entities.length > 1 ? (
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm">Entity</Label>
              <Select value={entityId} onValueChange={setEntityId}>
                <SelectTrigger size="sm" aria-label="Entity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {company.entities.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.legal_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {company.job_descriptions.length ? (
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm">Job description</Label>
              <Select value={jobId} onValueChange={setJobId}>
                <SelectTrigger size="sm" aria-label="Job description">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Pick Later</SelectItem>
                  {company.job_descriptions.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!picked.size || busy}
            onClick={() => void submit()}
          >
            Link {picked.size || ""} {picked.size === 1 ? "case" : "cases"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
